// src/pages/Questionnaire.jsx
import React, { useEffect, useState, useMemo, useContext } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  Zoom,
  IconButton,
  Popover,
  Fade,
  Breadcrumbs,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import Grid4x4OutlinedIcon from '@mui/icons-material/Grid4x4Outlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import UsbOutlinedIcon from '@mui/icons-material/UsbOutlined';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { request } from "../utils/request";
import { ColorModeContext } from "../index";

export default function Questionnaire() {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';
  const [data, setData] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverContent, setPopoverContent] = useState("");

  const questions = [
    {
      key: "osVersion",
      label: "OS Version",
      description: "Ensure the device is running a supported and up-to-date operating system version.",
      logic: "Latest OS build and within 60 days of recent security updates is considered secure. Older builds reduce compliance.",
      icon: <DesktopWindowsOutlinedIcon sx={{ color: '#1976d2' }} />,
    },
    {
      key: "securityPatches",
      label: "Security Patches",
      description: "Verify that all critical security patches are installed.",
      logic: "All critical and cumulative patches must be applied. Missing updates increase risk of known exploit exposure.",
      icon: <SecurityOutlinedIcon sx={{ color: '#78909c' }} />,
    },
    {
      key: "firewall",
      label: "Firewall",
      description: "Ensure the device firewall is enabled and properly configured.",
      logic: "All profiles (Domain, Private, Public) should be enabled. Disabled firewall layers create network vulnerability.",
      icon: <Grid4x4OutlinedIcon sx={{ color: '#e53935' }} />,
    },
    {
      key: "antivirus",
      label: "Antivirus",
      description: "Confirm an approved antivirus solution is installed and active.",
      logic: "A registered and updated antivirus must be installed and active. Outdated or missing antivirus increases malware risk.",
      icon: <GppGoodOutlinedIcon sx={{ color: '#43a047' }} />,
    },
    {
      key: "realTimeProtection",
      label: "Real-Time Protection",
      description: "Ensure real-time protection is enabled.",
      logic: "Real-time scanning should be active to detect threats immediately. If off, malware may go undetected until manual scans.",
      icon: <VerifiedUserOutlinedIcon sx={{ color: '#4caf50' }} />,
    },
    {
      key: "diskEncrypted",
      label: "Disk Encrypted",
      description: "Verify that full disk encryption is enabled.",
      logic: "Full-disk encryption (e.g., BitLocker) ensures data protection in case of theft. Unencrypted drives expose sensitive data.",
      icon: <StorageOutlinedIcon sx={{ color: '#1e88e5' }} />,
    },
    {
      key: "secureBootEnabled",
      label: "Secure Boot Enabled",
      description: "Confirm that secure boot is enabled in device firmware (BIOS/UEFI).",
      logic: "Secure Boot prevents boot-time rootkits and firmware tampering. Must be enabled in BIOS/UEFI.",
      icon: <PowerSettingsNewOutlinedIcon sx={{ color: '#1976d2' }} />,
    },
    {
      key: "trustedCAs",
      label: "Trusted CAs",
      description: "Ensure only trusted certificate authorities are configured.",
      logic: "Only trusted root certificates should exist. Presence of self-signed or interception CAs (e.g., proxy CA) reduces integrity.",
      icon: <WorkspacePremiumOutlinedIcon sx={{ color: '#1976d2' }} />,
    },
    {
      key: "usbDevices",
      label: "USB Devices",
      description: "Verify USB device access is restricted per policy.",
      logic: "No unauthorized removable drives should be connected. Restricting external media reduces risk of malware injection.",
      icon: <UsbOutlinedIcon sx={{ color: '#757575' }} />,
    },
    {
      key: "networkPrivilege",
      label: "Network & Privilege",
      description: "Ensure network configuration and user privileges follow security policy.",
      logic: "User accounts should operate with least privilege (non-admin). Admin rights should be limited to IT personnel only.",
      icon: <LanOutlinedIcon sx={{ color: '#1976d2' }} />,
    },
  ];

  // Fetch initial data
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await request("questionnaire", { method: "GET" });
      const entry = Array.isArray(res) ? res[0] : res;
      setData(entry || {});
      setEditedData(entry || {});
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleToggle = (key, value) => {
    setEditedData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const method = data && data._id ? "PUT" : "POST";
      const url = data && data._id ? `questionnaire/${data._id}` : "questionnaire";
      await request(url, {
        method,
        body: JSON.stringify(editedData),
        headers: { "Content-Type": "application/json" },
      });
      setConfirmOpen(false);
      fetchData();
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleInfoClick = (event, logic) => {
    setAnchorEl(event.currentTarget);
    setPopoverContent(logic);
  };

  const handleInfoClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);

  const unsavedChanges = useMemo(() => {
    if (!data) return false;
    return questions.some((q) => data[q.key] !== editedData[q.key]);
  }, [data, editedData]);

  const answeredCount = useMemo(() => {
    return questions.filter((q) => editedData[q.key]).length;
  }, [editedData]);

  const progress = useMemo(() => {
    return Math.round((answeredCount / questions.length) * 100);
  }, [answeredCount]);

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress className="text-interactive" />
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, md: 4 }} sx={{ backgroundColor: isDark ? 'transparent' : '#f4f6f8', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'flex-start' }, gap: { xs: 3, md: 0 }, mb: 4 }}>
        <Box flex={1} mr={{ xs: 0, md: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.95rem', mb: 1 }}>
            <Link to="/home" className="global-link" style={{ textDecoration: "none", color: '#1976d2' }}>
              Dashboard
            </Link>
            <Typography color="text.primary" sx={{ fontSize: '0.95rem', margin: 0 }}>
              Compliance Questionnaire
            </Typography>
          </Breadcrumbs>
          
          <Box display="flex" alignItems="center" gap={2} mb={1}>

            {unsavedChanges && (
              <Button
                variant="contained"
                className="global-button global-button-primary"
                startIcon={<SaveIcon />}
                onClick={() => setConfirmOpen(true)}
                sx={{ px: 2, minHeight: '32px' }}
              >
                Save Changes
              </Button>
            )}
          </Box>
          
          <Typography className="text-secondary" sx={{ fontSize: "14px", color: isDark ? '#94A3B8' : '#5c6c7f' }}>
            Each control contributes equally (10 points) to your overall risk score. Review and toggle each according to your system configuration.<br />
            Click <InfoOutlinedIcon sx={{ fontSize: "16px", verticalAlign: "middle", mb: "2px", color: "#1976d2" }} /> for detailed evaluation logic.
          </Typography>
        </Box>
        
        {/* Overall Progress Card */}
        <Box 
          sx={{ 
            width: { xs: '100%', md: '350px' }, 
            backgroundColor: isDark ? 'var(--canvas)' : '#fff', 
            borderRadius: '8px', 
            border: isDark ? '1px solid var(--hairline)' : '1px solid #e0e6ed', 
            p: 3,
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
          }}
        >
          <Box display="flex" justifyContent="space-between" mb={1.5}>
            <Typography sx={{ fontWeight: 700, color: isDark ? '#F8FAFC' : '#0f2744' }}>Overall Progress</Typography>
            <Typography sx={{ fontSize: '14px', color: isDark ? '#94A3B8' : '#5c6c7f' }}>{progress}% complete</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 12,
              borderRadius: 6,
              backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "#e0e6ed",
              mb: 2,
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#00a88f",
                borderRadius: 6,
              }
            }}
          />
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircleOutlineIcon sx={{ color: '#00a88f', fontSize: '20px' }} />
            <Typography sx={{ fontSize: '13px', color: isDark ? '#F8FAFC' : '#0f2744', fontWeight: 500 }}>
              {answeredCount} of {questions.length} controls completed
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Container */}
      <Box sx={{ 
        backgroundColor: isDark ? 'var(--canvas)' : '#fff', 
        borderRadius: '12px',
        border: isDark ? '1px solid var(--hairline)' : '1px solid #E5E7EB',
        overflowX: 'auto',
        overflowY: 'hidden'
      }}>

        {/* Questionnaire Table */}
        <table style={{ width: '100%', minWidth: '700px', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead>
            <tr>
              {[
                { label: '#', width: 60, align: 'left' },
                { label: 'Control Item', width: 250, align: 'left' },
                { label: 'Description', width: 'auto', align: 'left' },
                { label: 'Status', width: 120, align: 'left' }
              ].map((col, i) => (
                <th
                  key={i}
                  style={{
                    padding: '16px 24px',
                    width: col.width,
                    textAlign: col.align,
                    backgroundColor: isDark ? 'var(--canvas)' : '#ffffff',
                    color: isDark ? 'var(--vault-navy)' : '#0B1D51',
                    borderBottom: isDark ? '1px solid var(--hairline)' : '1px solid #E5E7EB',
                    fontFamily: 'Arial, sans-serif',
                    fontWeight: 700,
                    fontSize: '13px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {questions.map((q, idx) => {
              const val = editedData[q.key];
              const isYes = String(val).toUpperCase() === "YES";
              const isNo = !isYes && val !== undefined && val !== "";
              const changed = data && val !== data[q.key];

              return (
                <tr 
                  key={q.key} 
                  style={{ 
                    backgroundColor: changed ? "rgba(25, 118, 210, 0.04)" : "transparent",
                    borderBottom: isDark ? '1px solid var(--hairline)' : '1px solid #F3F4F6',
                    transition: "background-color 0.2s ease"
                  }}
                >
                  <td style={{ padding: "20px 24px", color: isDark ? 'var(--vault-navy)' : '#4B5563', fontSize: '14px', fontWeight: 500 }}>
                    {idx + 1}
                  </td>
                  <td style={{ padding: "20px 24px" }}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      {q.icon}
                      <Typography sx={{ fontWeight: 600, color: isDark ? 'var(--vault-navy)' : '#111827', fontSize: '14.5px' }}>
                        {q.label}
                      </Typography>
                      <Tooltip title="View evaluation logic">
                        <IconButton
                          size="small"
                          onClick={(e) => handleInfoClick(e, q.logic)}
                          sx={{ p: 0, color: "#8a9bb0" }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: '18px' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </td>
                  <td style={{ padding: "20px 24px" }}>
                    <Typography sx={{ color: isDark ? 'var(--slate)' : '#4B5563', fontSize: '14px' }}>
                      {q.description}
                    </Typography>
                  </td>
                  <td style={{ padding: "20px 24px" }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title="Mark as YES">
                        <IconButton
                          size="small"
                          sx={{
                            width: 32,
                            height: 32,
                            border: isYes ? "1.5px solid #00a88f" : (isDark ? "1.5px solid rgba(255,255,255,0.1)" : "1.5px solid #e0e6ed"),
                            backgroundColor: "transparent",
                            color: isYes ? "#00a88f" : (isDark ? "var(--slate)" : "#8a9bb0"),
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "rgba(0, 168, 143, 0.08)",
                            },
                          }}
                          onClick={() => handleToggle(q.key, "YES")}
                        >
                          <DoneIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Mark as NO">
                        <IconButton
                          size="small"
                          sx={{
                            width: 32,
                            height: 32,
                            border: isNo ? "1.5px solid #d6323a" : (isDark ? "1.5px solid rgba(255,255,255,0.1)" : "1.5px solid #e0e6ed"),
                            backgroundColor: "transparent",
                            color: isNo ? "#d6323a" : (isDark ? "var(--slate)" : "#8a9bb0"),
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "rgba(214, 50, 58, 0.08)",
                            },
                          }}
                          onClick={() => handleToggle(q.key, "NO")}
                        >
                          <CloseIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>

      {/* Info Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleInfoClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={Zoom}
        transitionDuration={200}
        PaperProps={{ 
          sx: { 
            mt: 1.5,
            p: 2.5, 
            maxWidth: 320,
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(15, 39, 68, 0.12), 0 2px 8px rgba(15, 39, 68, 0.08)',
            border: '1px solid rgba(224, 230, 237, 0.8)',
            backgroundColor: '#ffffff',
            position: 'relative',
            overflow: 'visible',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-6px',
              left: '50%',
              transform: 'translateX(-50%) rotate(45deg)',
              width: '12px',
              height: '12px',
              backgroundColor: '#ffffff',
              borderTop: '1px solid rgba(224, 230, 237, 0.8)',
              borderLeft: '1px solid rgba(224, 230, 237, 0.8)',
            }
          } 
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <InfoOutlinedIcon sx={{ color: '#1976d2', fontSize: '20px' }} />
          <Typography sx={{ fontWeight: 700, color: '#0f2744', fontSize: '15px' }}>
            Evaluation Logic
          </Typography>
        </Box>
        <Typography sx={{ color: '#5c6c7f', fontSize: '14px', lineHeight: 1.6 }}>
          {popoverContent}
        </Typography>
      </Popover>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{ className: 'global-card', sx: { minHeight: 'auto' } }}
      >
        <DialogTitle className="global-dialog-title">
          Confirm Save
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography className="text-secondary">
            You've modified one or more responses. Do you want to save these updates to the database?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid var(--hairline)" }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            className="global-button global-button-outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            className="global-button global-button-primary"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}